import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalTitle, 
  ModalBody,
  ModalFooter,
  Button,
  Alert
} from '../../styles/components';
import {
  ClipboardDocumentIcon,
  ShieldExclamationIcon,
  KeyIcon,
  EyeSlashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface SecretDisplayModalProps {
  isOpen: boolean;
  secret: string;
  title: string;
  onComplete: () => void;
  secretType?: 'client-secret' | 'api-key';
  clientId?: string;
}

const SecretContainer = styled.div`
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.lg} 0;
`;

const SecretDisplay = styled.div`
  background: ${props => props.theme.colors.gray[50]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.md};
  position: relative;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: ${props => props.theme.fontSizes.sm};
  word-break: break-all;
  user-select: all;
  cursor: text;
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

const CopyButton = styled(Button)`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  margin: ${props => props.theme.spacing.lg} 0;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  margin: 0;
  cursor: pointer;
`;

const HelperText = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
  margin: ${props => props.theme.spacing.sm} 0;
`;

export function SecretDisplayModal({
  isOpen,
  secret,
  title,
  onComplete,
  secretType = 'client-secret',
  clientId
}: SecretDisplayModalProps) {
  const [copied, setCopied] = useState(false);
  const [copiedClientId, setCopiedClientId] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes auto-timeout
  const secretRef = useRef<HTMLDivElement>(null);
  const clientIdRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Security: Clear secret from memory after component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Auto-timeout for security
  useEffect(() => {
    if (isOpen && secret) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        handleComplete();
      }, 300000); // 5 minutes
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, secret]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      if (secretRef.current) {
        secretRef.current.focus();
        document.execCommand('selectAll');
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    }
  }, [secret]);

  const handleCopyClientId = useCallback(() => {
    if (clientId && clientIdRef.current) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(clientId);
        setCopiedClientId(true);
        setTimeout(() => setCopiedClientId(false), 3000);
      } else {
        clientIdRef.current.focus();
        document.execCommand('selectAll');
        document.execCommand('copy');
        setCopiedClientId(true);
        setTimeout(() => setCopiedClientId(false), 3000);
      }
    }
  }, [clientId]);

  const handleComplete = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    onComplete();
  }, [onComplete]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <Modal $isOpen={isOpen}>
      <ModalOverlay onClick={undefined} /> {/* Prevent accidental closure */}
      <ModalContent style={{ maxWidth: '700px', position: 'relative' }}>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <div style={{ 
            fontSize: '0.875rem', 
            color: '#ef4444',
            fontWeight: '500'
          }}>
            Auto-close: {formatTime(timeRemaining)}
          </div>
        </ModalHeader>
        
        <ModalBody>
          <Alert variant="error" style={{ marginBottom: '1.5rem' }}>
            <strong>Important:</strong> This secret will only be shown once. Save it immediately.
          </Alert>

          {clientId && (
            <>
              <div style={{ marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>
                Client ID
              </div>
              <SecretContainer>
                <SecretDisplay
                  ref={clientIdRef}
                  tabIndex={0}
                  role="textbox"
                  aria-label="Client ID - copy this for authentication"
                  onClick={() => clientIdRef.current?.focus()}
                >
                  {clientId}
                  <CopyButton size="sm" onClick={handleCopyClientId} aria-label="Copy client ID to clipboard">
                    {copiedClientId ? <CheckCircleIcon /> : <ClipboardDocumentIcon />}
                    {copiedClientId ? 'Copied' : 'Copy'}
                  </CopyButton>
                </SecretDisplay>
              </SecretContainer>
            </>
          )}

          <div style={{ marginBottom: '0.5rem', marginTop: clientId ? '1rem' : '0', fontWeight: 500, color: '#374151' }}>
            Secret
          </div>
          <SecretContainer>
            <SecretDisplay
              ref={secretRef}
              tabIndex={0}
              role="textbox"
              aria-label="Generated secret - copy this immediately"
              onClick={() => secretRef.current?.focus()}
            >
              {secret}
              <CopyButton size="sm" onClick={handleCopy} aria-label="Copy secret to clipboard">
                {copied ? <CheckCircleIcon /> : <ClipboardDocumentIcon />}
                {copied ? 'Copied' : 'Copy'}
              </CopyButton>
            </SecretDisplay>
          </SecretContainer>

          <HelperText>
            • Save this in your password manager<br/>
            • Never commit to version control<br/>
            • Generate a new secret if lost or compromised
          </HelperText>

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>I have saved this secret securely and understand it cannot be viewed again.</span>
          </CheckboxContainer>
        </ModalBody>
        
        <ModalFooter>
          <Button
            onClick={handleComplete}
            disabled={!acknowledged}
          >
            Complete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}